// Bundled ingredient_info seed data.
//
// This module exports an array that mirrors the ingredient_info table shape
// in Supabase: { ingredient_id, info } per row. The auto-seeder
// (src/lib/seedIngredientInfo.js) upserts these rows on first login so
// users never need to run SQL by hand.
//
// As more ingredient metadata moves from the JS INGREDIENT_INFO object in
// src/data/ingredients.js to this DB-backed format, append entries here
// and bump SEED_VERSION below to force a re-seed for existing users.
//
// Same shape that getIngredientInfo() in ingredients.js consumes — see
// the comment block at the top of that file for the full schema.

// Bump this when seed data changes so existing users get the update.
// The seeder gates on a localStorage key tied to this version.
export const SEED_VERSION = 9;

export const SEED_INGREDIENT_INFO = [
  {
    ingredient_id: "kosher_salt",
    info: {
      description: "Coarse-grained salt with no additives. The default cooking salt for professionals — the large flakes are easy to pinch and distribute evenly, and the open crystal structure dissolves quickly on contact with moisture.",
      flavorProfile: "Pure salt, clean, no metallic or bitter aftertaste",
      prepTips: "Pinch from a bowl, not a shaker. Season from 12 inches above so it distributes. Taste as you go — you can always add, never subtract.",
      storage: {
        location: "pantry",
        shelfLifeDays: 9999,
        shelfLife: { fridge: null, freezer: null, pantry: 9999 },
        tips: "Indefinite shelf life. Keep in a wide-mouth bowl or cellar near the stove for easy pinching.",
        spoilageSigns: "Doesn't spoil. Clumping just means it absorbed moisture — break it up and use it.",
        freezable: false,
      },
      substitutions: [
        { id: "sea_salt", tier: "direct", note: "Slightly different crystal size; adjust by taste." },
        { id: "table_salt", tier: "caution", note: "Much denser per volume — use half the amount or measure by weight." },
        { id: "flaky_salt", tier: "creative", note: "Use as a finisher, not for cooking — too expensive and the texture's wasted." },
      ],
      pairs: ["black_pepper", "garlic", "lemon", "butter", "olive_oil"],
      flavor: {
        primary: ["salt"],
        intensity: "mild",
        heatChange: {
          raw: "clean salt, no aftertaste",
          cooked: "dissolves into the dish, becomes invisible — its job is to amplify, not to be tasted",
          charred: "doesn't change — salt doesn't burn",
        },
      },
      nutrition: { per: "1 tsp", kcal: 0, sodium_mg: 1120 },
      origin: "Historically produced for the kosher meat-curing process (koshering), not because the salt itself is kosher. Diamond Crystal and Morton are the two dominant brands in the US, with meaningfully different densities.",
      culturalNotes: "Diamond Crystal vs Morton is the great kitchen schism. Diamond is ~40% less dense per teaspoon — recipes that just say 'kosher salt' usually mean Diamond. If you use Morton, scale down by about a third or you'll oversalt every dish.",
      recipes: ["Almost everything savory", "Brines", "Pasta water", "Meat dry rubs"],
      allergens: [],
      diet: { vegan: true, vegetarian: true, keto: true, halal: true, kosher: "pareve", fodmap: "low", nightshade: false, allium: false, glutenFree: true },
      seasonality: { yearRound: true },
      sourcing: "Diamond Crystal for cooking (hollow flakes, dissolves fast, easy to pinch). Morton for baking where you need consistent weight-per-volume.",
      market: {
        priceTier: "budget",
        availability: "supermarket",
        organicCommon: false,
        qualityMatters: false,
        qualityNote: "It's salt. Brand matters for measurement consistency, not flavor — every kosher salt tastes the same. Pick Diamond Crystal because every recipe is calibrated for it.",
      },
      skillDev: { skills: ["seasoning"], difficulty: "easy", proFromScratch: false, fromScratchRecipeId: null },
    },
  },
  {
    ingredient_id: "black_pepper",
    info: {
      description: "Dried unripe berries of Piper nigrum, the most traded spice in human history. The piperine compound delivers the heat; volatile oils deliver the aroma. Freshly ground is non-negotiable — pre-ground loses potency within weeks.",
      flavorProfile: "Sharp, warm, piney, slightly floral. Tellicherry peppercorns (left to ripen longer on the vine) are fruitier and more complex.",
      prepTips: "Always grind fresh — a pepper mill is the single biggest upgrade for a new cook. Add early for background warmth, add at the end for aromatic punch. Toast whole peppercorns in a dry pan to bloom the oils before crushing for cacio e pepe.",
      storage: {
        location: "pantry",
        shelfLifeDays: 1095,
        shelfLife: { fridge: null, freezer: 1825, pantry: 1095 },
        tips: "Whole peppercorns last 3+ years. Ground pepper loses potency in 3-6 months. Keep in a mill, not a shaker.",
        spoilageSigns: "No aroma when freshly ground = it's done. Grayish powder instead of a sharp peppery smell.",
        freezable: true,
        freezeNotes: "Whole peppercorns freeze indefinitely. Doubles their shelf life. Bring to room temp before grinding.",
      },
      substitutions: [
        { id: "white_pepper", tier: "direct", note: "Same plant, different processing — hotter, less aromatic, no dark specks in light sauces." },
        { id: "peppercorns", tier: "direct", note: "Whole form of the same thing. Grind fresh for better flavor." },
      ],
      pairs: ["salt", "lemon", "garlic", "steak", "eggs", "pasta", "butter", "parmesan"],
      flavor: {
        primary: ["spicy", "umami"],
        intensity: "moderate",
        heatChange: {
          raw: "sharp, biting, immediate heat that hits the front of the tongue",
          cooked: "background warmth, mellower; piperine survives heat but volatile oils evaporate fast",
          charred: "becomes acrid and harsh — never let pepper hit a dry pan alone",
        },
      },
      nutrition: { per: "1 tsp", kcal: 6, protein_g: 0.2, fat_g: 0.1, carb_g: 1.5, fiber_g: 0.6 },
      origin: "Native to Kerala, India (Malabar Coast). The spice that launched the Age of Exploration — Europeans sailed around Africa and across oceans to bypass the Arab spice monopoly.",
      culturalNotes: "Pepper was literally worth its weight in gold in medieval Europe. Rent, taxes, and dowries were paid in peppercorns. The phrase 'peppercorn rent' (a token payment) survives in English law to this day.",
      recipes: ["Cacio e Pepe", "Steak au Poivre", "Pepper-crusted tuna", "Bistro French onion soup"],
      allergens: [],
      diet: { vegan: true, vegetarian: true, keto: true, halal: true, kosher: "pareve", fodmap: "low", nightshade: false, allium: false, glutenFree: true },
      seasonality: { yearRound: true },
      sourcing: "Tellicherry peppercorns (graded TGSEB or TGEB) are the largest 10% of the Malabar harvest — sweeter, fruitier, more complex. Worth the ~$2 premium per ounce. Pre-ground is the kitchen equivalent of decaf.",
      market: {
        priceTier: "moderate",
        availability: "supermarket",
        organicCommon: true,
        qualityMatters: true,
        qualityNote: "Generic supermarket peppercorns are fine for everyday seasoning. For finishing dishes where pepper IS the flavor (cacio e pepe, steak au poivre), Tellicherry is a noticeable upgrade.",
      },
      skillDev: { skills: ["seasoning", "knife"], difficulty: "easy", proFromScratch: false, fromScratchRecipeId: null },
    },
  },
  {
    ingredient_id: "paprika",
    info: {
      description: "Dried, ground red peppers — the baseline paprika. Ranges from sweet and mild (Hungarian édesnemes) to moderately warm. The workhorse behind goulash, deviled eggs, and anything that needs warm red color without serious heat.",
      flavorProfile: "Sweet, warm, mildly earthy, fruity. Almost no heat — the sweetness of dried pepper is the point.",
      prepTips: "Bloom in hot oil or butter for 15-30 seconds to activate the fat-soluble pigments. Never add to screaming-hot oil — it scorches instantly and turns bitter.",
      storage: {
        location: "pantry",
        shelfLifeDays: 730,
        shelfLife: { fridge: null, freezer: 1460, pantry: 730 },
        tips: "Loses color and potency faster than most spices. Replace every 1-2 years. If it smells like nothing, it IS nothing.",
        spoilageSigns: "Dull brown-red instead of vibrant red, no aroma when you open the jar, clumping from absorbed moisture.",
        freezable: true,
        freezeNotes: "Double bag it — picks up freezer odors through cardboard. Doubles the usable life.",
      },
      substitutions: [
        { id: "smoked_paprika", tier: "direct", note: "Adds smokiness — great for some dishes, overpowering for others." },
        { id: "cayenne", tier: "caution", note: "10-20x hotter. Use a pinch where you would use a teaspoon." },
        { id: "chili_powder", tier: "creative", note: "More complex, more savory — works in chili, tacos, rubs." },
      ],
      pairs: ["garlic", "onion", "chicken", "potato", "eggs", "sour_cream"],
      flavor: {
        primary: ["sweet", "umami"],
        intensity: "mild",
        heatChange: {
          raw: "dusty, powdery, faintly bitter",
          cooked: "sweet, deep red, fruity — bloomed in fat it turns warm and almost caramel",
          charred: "bitter and acrid within seconds — burns faster than any other spice",
        },
      },
      nutrition: { per: "1 tsp", kcal: 6, protein_g: 0.3, fat_g: 0.3, carb_g: 1.2, fiber_g: 0.8 },
      origin: "Central Mexico (the peppers), brought to Spain and Hungary by the 16th century. Hungary made it the national spice — Szeged and Kalocsa are the two great paprika regions.",
      culturalNotes: "Hungary classifies paprika into eight grades from különleges (delicate, bright red) to erős (hot). The Nobel Prize for Vitamin C was awarded for work on Hungarian paprika — it has more C per gram than citrus.",
      recipes: ["Chicken Paprikash", "Hungarian Goulash", "Deviled Eggs", "Patatas Bravas"],
      allergens: ["nightshade"],
      allergenDetail: "Nightshade family (Solanaceae) — relevant for autoimmune elimination diets but not a true food allergy for most people.",
      diet: { vegan: true, vegetarian: true, keto: true, halal: true, kosher: "pareve", fodmap: "low", nightshade: true, allium: false, glutenFree: true },
      seasonality: { yearRound: true },
      sourcing: "Hungarian (Szeged or Kalocsa) for complexity and depth. Spanish pimentón dulce is fruitier. Avoid generic supermarket paprika if you cook seriously — the quality gap is bigger than you think.",
      market: {
        priceTier: "budget",
        availability: "supermarket",
        organicCommon: false,
        qualityMatters: true,
        qualityNote: "The $3 supermarket jar vs $10 Hungarian Szegedi is a genuine night-and-day upgrade for any dish where paprika is the lead spice (goulash, paprikash). For color-only uses (deviled eggs, sprinkle on hummus), cheap is fine.",
      },
      skillDev: { skills: ["seasoning", "heat"], difficulty: "easy", proFromScratch: false, fromScratchRecipeId: null },
    },
  },
  {
    ingredient_id: "cumin",
    info: {
      description: "Dried seeds of Cuminum cyminum, used whole or ground. The warm, earthy backbone of Mexican, Indian, Middle Eastern, and North African cooking. If your chili, curry, or falafel tastes flat, it needs more cumin.",
      flavorProfile: "Warm, earthy, slightly nutty, faintly bitter. Toasting intensifies the nuttiness dramatically.",
      prepTips: "Toast whole seeds in a dry pan for 60 seconds until fragrant before grinding — the flavor difference is night and day. Pairs with coriander so often they're practically one spice (the classic 2:1 cumin:coriander ratio in most Indian and Middle Eastern blends).",
      storage: {
        location: "pantry",
        shelfLifeDays: 1095,
        shelfLife: { fridge: null, freezer: 1825, pantry: 1095 },
        tips: "Whole seeds last 3-4 years. Ground loses punch within 6 months — buy whole and grind as needed if you can.",
        spoilageSigns: "Faded color, no aroma when toasted in a dry pan. If toasting doesn't wake it up, it's gone.",
        freezable: true,
        freezeNotes: "Whole seeds in a sealed jar — adds 2 years. Bring to room temp before grinding to avoid clumping.",
      },
      substitutions: [
        { id: "ground_cumin", tier: "direct", note: "Same spice, pre-ground. Convenience over flavor." },
        { id: "coriander", tier: "creative", note: "Different flavor but fills the same warm-earthy role in a blend. Use 1.5x the cumin amount." },
        { id: "chili_powder", tier: "creative", note: "Already contains cumin (~25-30%) plus other warming spices. Can stand in for cumin in Tex-Mex applications." },
      ],
      pairs: ["coriander", "garlic", "chili_powder", "lime", "beans", "onion", "yogurt", "tomato"],
      flavor: {
        primary: ["umami", "bitter"],
        intensity: "moderate",
        heatChange: {
          raw: "dusty, faintly medicinal — needs heat to come alive",
          cooked: "warm, nutty, deeply earthy — toasted seeds are floral",
          charred: "smoky and bitter; burnt cumin tastes like petrol",
        },
      },
      nutrition: { per: "1 tsp", kcal: 8, protein_g: 0.4, fat_g: 0.5, carb_g: 0.9, fiber_g: 0.2, iron_mg: 1.4 },
      origin: "Eastern Mediterranean — cultivated in Egypt and the Levant for at least 4,000 years. India is now the dominant producer, growing 70% of the world's supply.",
      culturalNotes: "Cumin is the second most consumed spice worldwide after black pepper. In Ayurvedic medicine it's considered a digestive aid — jeera water (cumin tea) is sipped daily across India. The spice that defines the spice route from Asia to the Americas, brought to Mexico by Spanish friars in the 1500s.",
      recipes: ["Chili con Carne", "Falafel", "Tikka Masala", "Hummus", "Mole Rojo", "Cumin Lamb (Xinjiang)"],
      allergens: [],
      diet: { vegan: true, vegetarian: true, keto: true, halal: true, kosher: "pareve", fodmap: "low", nightshade: false, allium: false, glutenFree: true },
      seasonality: { yearRound: true },
      sourcing: "Whole seeds from a high-turnover spice shop will outperform a 5-year-old supermarket ground jar by a mile. If you only buy one whole spice to grind fresh, make it cumin.",
      market: {
        priceTier: "budget",
        availability: "supermarket",
        organicCommon: true,
        qualityMatters: true,
        qualityNote: "Indian/Middle Eastern grocers sell cumin for half the supermarket price and it's almost always fresher. The supermarket ground version in the small jar is the worst-case scenario — old, dusty, dim.",
      },
      skillDev: { skills: ["seasoning", "heat"], difficulty: "easy", proFromScratch: false, fromScratchRecipeId: null },
    },
  },
  {
    ingredient_id: "garlic_powder",
    info: {
      description: "Dehydrated garlic, ground fine. Delivers garlic flavor without the moisture, texture, or burn of fresh — it dissolves into dry rubs, marinades, and doughs where fresh garlic can't go. Not a substitute for fresh in sautés; a complement to it.",
      flavorProfile: "Mellow, sweet, roasted-garlic character. Less sharp and pungent than fresh — the dehydration tames the allicin bite.",
      prepTips: "Hydrate in a few drops of water before adding to wet dishes — prevents clumping and helps it bloom. For dry rubs and seasoning blends, use straight. Do NOT confuse with garlic salt (which is ~75% salt — measuring it like garlic powder will oversalt every dish).",
      storage: {
        location: "pantry",
        shelfLifeDays: 1095,
        shelfLife: { fridge: null, freezer: 1825, pantry: 1095 },
        tips: "Hygroscopic — absorbs moisture and clumps. A clumped jar is past its prime. Add a few grains of rice to the jar to absorb moisture in humid kitchens.",
        spoilageSigns: "Hard rock-like clumps that won't break up, faded color (should be cream/beige, not gray), no aroma when opened.",
        freezable: true,
        freezeNotes: "Freezing extends shelf life dramatically. Use straight from the freezer — no need to thaw before measuring.",
      },
      substitutions: [
        { id: "garlic", tier: "creative", note: "Fresh garlic — more pungent, adds moisture. ⅛ tsp powder ≈ 1 clove. Won't disperse evenly in dry applications." },
        { id: "garlic_salt", tier: "caution", note: "75% salt by weight. Reduce other salt in the recipe by half if substituting 1:1." },
        { id: "onion_powder", tier: "creative", note: "Different aromatic but fills the same dry-rub role. Often used together anyway." },
      ],
      pairs: ["onion_powder", "paprika", "black_pepper", "cumin", "oregano", "salt", "thyme"],
      flavor: {
        primary: ["umami", "sweet"],
        intensity: "moderate",
        heatChange: {
          raw: "mild, sweet, slightly dusty",
          cooked: "deeply savory, roasted-garlic warmth — blooms in fat",
          charred: "bitter and acrid; burns much faster than fresh garlic because there's no moisture to protect it",
        },
      },
      nutrition: { per: "1 tsp", kcal: 10, protein_g: 0.5, fat_g: 0, carb_g: 2.3, fiber_g: 0.3 },
      origin: "Dehydrated garlic originated in commercial food processing (mid-20th century US). Garlic itself is Central Asian — cultivated for 5,000+ years. China grows ~80% of the world's commercial garlic supply.",
      culturalNotes: "Snobs dismiss garlic powder but professional kitchens use it constantly. It does something fresh garlic literally cannot: distribute evenly in a dry rub, season a burger patty throughout, or flavor bread dough without wet pockets. Different tools for different jobs — chefs who only use fresh garlic are working with one hand tied behind their back.",
      recipes: ["Dry Rubs (BBQ)", "Garlic Bread", "Burger Seasoning", "Ranch Dressing", "Caesar Dressing", "Pretzel Bread"],
      allergens: ["allium"],
      allergenDetail: "Allium family (onions, garlic, leeks, shallots). FODMAP-sensitive folks react strongly to all alliums; garlic powder is just as problematic as fresh.",
      diet: { vegan: true, vegetarian: true, keto: true, halal: true, kosher: "pareve", fodmap: "high", nightshade: false, allium: true, glutenFree: true },
      seasonality: { yearRound: true },
      sourcing: "Avoid the cheapest white-label brands — quality varies wildly. McCormick, Spice Islands, Penzeys, and Burlap & Barrel are reliable. Granulated garlic (slightly coarser than powder) is what most pro kitchens actually stock — same flavor, less clumping.",
      market: {
        priceTier: "budget",
        availability: "supermarket",
        organicCommon: true,
        qualityMatters: true,
        qualityNote: "The $2 generic vs $6 Penzeys gap is real — the cheap stuff often tastes flat and dusty. Worth the upgrade for any spice you use weekly.",
      },
      skillDev: { skills: ["seasoning"], difficulty: "easy", proFromScratch: false, fromScratchRecipeId: null },
    },
  },
  {
    ingredient_id: "italian_seasoning",
    info: {
      description: "A dried herb blend representing the baseline of Italian-American cooking. Not traditional to any specific Italian region — it's an American pantry shortcut that combines the herbs you'd find in a Neapolitan nonna's garden into one shaker.",
      flavorProfile: "Herbal, warm, earthy, slightly floral. The oregano-basil-thyme trio does most of the work; the others add nuance.",
      prepTips: "Crush between your palms before adding to wake up the oils — the friction releases volatile compounds that are dormant in the dried form. Add early to marinara, pizza sauce, and braises so the flavors meld; add late to vinaigrettes and finishing applications.",
      storage: {
        location: "pantry",
        shelfLifeDays: 730,
        shelfLife: { fridge: null, freezer: 1095, pantry: 730 },
        tips: "Blends age faster than single spices because each herb degrades at a different rate — the basil fades before the rosemary. Replace yearly for best results.",
        spoilageSigns: "Faded gray-brown color (fresh blends are vibrant green-brown), no aroma when crushed between palms. Stale Italian seasoning tastes like dried grass.",
        freezable: true,
        freezeNotes: "Freezing slows the degradation of volatile herb oils. Doubles the practical shelf life. Use straight from the freezer.",
      },
      substitutions: [
        { id: "oregano", tier: "direct", note: "Oregano alone covers 60% of the blend's job. The single best substitute if you have to pick one." },
        { id: "herbs_de_provence", tier: "creative", note: "French counterpart — adds lavender and savory but similar overall character. Subbing in a French direction." },
        { id: "dried_basil", tier: "creative", note: "Plus dried oregano (1:1) gets you most of the way there." },
      ],
      blendOf: ["oregano", "dried_basil", "dried_thyme", "dried_rosemary", "dried_marjoram", "dried_sage"],
      pairs: ["garlic", "tomato", "olive_oil", "mozzarella", "pasta", "bread", "parmesan", "chicken"],
      flavor: {
        primary: ["umami", "bitter"],
        intensity: "moderate",
        heatChange: {
          raw: "grassy, dusty, faintly bitter — nothing magical until heat or fat unlocks it",
          cooked: "warm, herbal, layered — oregano forward, basil in the back, rosemary as the bass note",
          charred: "burnt-grass bitter; the herbs scorch fast in dry heat",
        },
      },
      nutrition: { per: "1 tsp", kcal: 3, protein_g: 0.1, fat_g: 0.1, carb_g: 0.6, fiber_g: 0.4 },
      origin: "American invention, mid-20th century. McCormick and other spice companies popularized it as a convenience product. The proportions vary by brand — some lean heavy on oregano, others on basil.",
      culturalNotes: "Italian grandmothers do not use 'Italian seasoning' — they use what's growing outside, in fresh form, picked that day. The dried-blend version is a uniquely American compromise: convenient, consistent, and good enough for weeknight cooking. Don't be precious about it; nonnas are also not making homemade pasta on a Tuesday.",
      recipes: ["Marinara Sauce", "Pizza Dough", "Garlic Bread", "Italian Vinaigrette", "Chicken Parmesan", "Lasagna Sauce", "Bruschetta", "Italian Sausage Seasoning"],
      allergens: [],
      diet: { vegan: true, vegetarian: true, keto: true, halal: true, kosher: "pareve", fodmap: "low", nightshade: false, allium: false, glutenFree: true },
      seasonality: { yearRound: true },
      sourcing: "Bulk-bin spice shops are dramatically fresher and cheaper than supermarket jars. The 'Italian Herb' blend at Penzeys, Burlap & Barrel, or any well-trafficked spice market beats McCormick by a wide margin.",
      market: {
        priceTier: "budget",
        availability: "supermarket",
        organicCommon: true,
        qualityMatters: true,
        qualityNote: "Quality drops fast as the blend ages. A fresh small-batch jar from a spice market makes a noticeably better marinara than a dusty supermarket jar that's been on the shelf for two years.",
      },
      skillDev: { skills: ["seasoning"], difficulty: "easy", proFromScratch: true, fromScratchRecipeId: null },
    },
  },

  // ── chili family ────────────────────────────────────────────────────
  {
    ingredient_id: "cayenne",
    info: {
      description: "Finely ground dried cayenne peppers. Pure, clean heat (30,000-50,000 Scoville) with minimal flavor complexity — it's a heat delivery mechanism, not a flavor spice. The kitchen equivalent of a volume knob for spiciness.",
      flavorProfile: "Sharp, biting heat with a slight fruity undertone. No smokiness, no sweetness — just capsaicin punch.",
      prepTips: "Start with ⅛ tsp and taste. You can always add more; you cannot remove heat. Dissolves into liquids invisibly — great for soups, sauces, mac and cheese, bloody marys. For slow-build warmth, add early; for sharp bite, add at the end.",
      storage: {
        location: "pantry",
        shelfLifeDays: 1095,
        shelfLife: { fridge: null, freezer: 1825, pantry: 1095 },
        tips: "Long shelf life — capsaicin is stable. Replace when bright red fades to dull brown.",
        spoilageSigns: "Dull brown color, no heat when tasted on the tongue (a fresh pinch should make you cough).",
        freezable: true,
        freezeNotes: "Doubles shelf life. Capsaicin laughs at freezer temperatures.",
      },
      substitutions: [
        { id: "red_pepper_flakes", tier: "direct", note: "Coarser texture, slightly different heat distribution. ½ tsp cayenne ≈ 1 tsp flakes." },
        { id: "chili_powder", tier: "caution", note: "Chili powder is a BLEND with cumin/garlic/oregano — much more complex, much less hot. Use 4-5x more." },
        { id: "hot_sauce", tier: "creative", note: "Adds vinegar acidity but delivers similar heat. Reduce other liquids." },
      ],
      pairs: ["garlic", "lemon", "butter", "chocolate", "honey", "lime", "tomato"],
      flavor: {
        primary: ["spicy"],
        intensity: "intense",
        heatChange: {
          raw: "immediate, sharp burn that builds — front of tongue first",
          cooked: "diffuses into liquid, becomes more pervasive but slightly less sharp",
          charred: "bitter and acrid; burnt cayenne is the worst kind of burnt",
        },
      },
      nutrition: { per: "1 tsp", kcal: 6, protein_g: 0.2, fat_g: 0.3, carb_g: 1, fiber_g: 0.5, vitamin_c_mg: 1.4 },
      origin: "Named after Cayenne, French Guiana, but cultivated worldwide in tropical and subtropical regions. India is now the largest commercial producer.",
      culturalNotes: "The capsaicin in cayenne triggers endorphin release — the pain-pleasure loop that makes spicy food addictive. Medicinally, capsaicin is the active ingredient in topical pain creams. Cayenne in hot chocolate dates to Aztec xocolatl traditions; the modern dark-chocolate-with-cayenne trend is borrowing a 700-year-old idea.",
      recipes: ["Buffalo Wing Sauce", "Mac and Cheese", "Bloody Mary", "Mexican Hot Chocolate", "Gumbo", "Deviled Eggs"],
      allergens: ["nightshade"],
      allergenDetail: "Nightshade family — relevant for autoimmune elimination diets.",
      diet: { vegan: true, vegetarian: true, keto: true, halal: true, kosher: "pareve", fodmap: "low", nightshade: true, allium: false, glutenFree: true },
      seasonality: { yearRound: true },
      sourcing: "Generic supermarket cayenne is fine — heat is heat. Specialty single-origin cayennes from spice markets occasionally have more fruity character but the difference is subtle.",
      market: {
        priceTier: "budget",
        availability: "supermarket",
        organicCommon: true,
        qualityMatters: false,
        qualityNote: "It's heat. Almost any brand delivers what you need. The expensive small-batch versions are mostly novelty.",
      },
      skillDev: { skills: ["seasoning", "heat"], difficulty: "easy", proFromScratch: false, fromScratchRecipeId: null },
    },
  },

  {
    ingredient_id: "smoked_paprika",
    info: {
      description: "Pimentón de la Vera — peppers dried over smoldering oak for two weeks, then ground. The smokiness is deep and real (not liquid smoke). Spanish cuisine's secret weapon and the easiest way to add depth to any savory dish.",
      flavorProfile: "Intensely smoky, sweet-warm, complex. Transforms anything it touches — half a teaspoon makes a bean soup taste like it simmered over a campfire all day.",
      prepTips: "A little goes far — start with ¼ tsp and build. Bloom in oil for maximum impact. Pairs insanely well with roasted vegetables, aioli, and anything tomato-based. Three grades to know: dulce (sweet), agridulce (bittersweet), picante (hot).",
      storage: {
        location: "pantry",
        shelfLifeDays: 730,
        shelfLife: { fridge: null, freezer: 1460, pantry: 730 },
        tips: "Keep sealed tightly — the smoke compounds are volatile and escape over time. Tin containers (the traditional Spanish packaging) preserve better than glass.",
        spoilageSigns: "When the smoke aroma fades on opening the jar, it's past its prime. Dull color, weak punch.",
        freezable: true,
        freezeNotes: "Freezing dramatically slows the loss of smoke compounds. Doubles usable life.",
      },
      substitutions: [
        { id: "paprika", tier: "direct", note: "Loses the smoke; add a drop of liquid smoke to compensate (or accept the loss — sometimes you just want the color)." },
        { id: "chipotle", tier: "creative", note: "Smoked jalapeño powder — adds smoke + significant heat. Different but in the same family." },
      ],
      pairs: ["chickpeas", "potato", "tomato", "aioli", "eggs", "chorizo", "garlic", "olive_oil"],
      flavor: {
        primary: ["umami", "sweet"],
        intensity: "intense",
        heatChange: {
          raw: "smoky, deep, slightly bitter — strong even before cooking",
          cooked: "rounds out, the smoke integrates into the dish, sweetness emerges",
          charred: "burns fast and turns acrid; the smoke compounds are already at their peak — heat past that point destroys them",
        },
      },
      nutrition: { per: "1 tsp", kcal: 6, protein_g: 0.3, fat_g: 0.3, carb_g: 1.2, fiber_g: 0.8 },
      origin: "La Vera valley, Extremadura, Spain. The peppers are exclusively oak-smoked — the method is protected by D.O. Pimentón de la Vera designation. Hungarian smoked paprika exists but is generally considered inferior.",
      culturalNotes: "Spanish chorizo gets its character from pimentón, not chili heat — it's a fundamentally different sausage from Mexican chorizo because of this one spice. The Spanish royal family has been issuing pimentón decrees since the 1500s; the king's monastery in Yuste made the first commercial production economically viable.",
      recipes: ["Patatas Bravas", "Chickpea Stew", "Aioli", "Spanish Chorizo", "Romesco Sauce", "Smoky Roasted Vegetables", "Paella (Marisco)"],
      allergens: ["nightshade"],
      diet: { vegan: true, vegetarian: true, keto: true, halal: true, kosher: "pareve", fodmap: "low", nightshade: true, allium: false, glutenFree: true },
      seasonality: { yearRound: true },
      sourcing: "Look for the D.O. Pimentón de la Vera seal — the difference between authentic Spanish and supermarket 'smoked paprika' is enormous. La Chinata and La Dalia are the gold standard. Around $8-12 for a tin that lasts a year.",
      market: {
        priceTier: "moderate",
        availability: "specialty",
        organicCommon: false,
        qualityMatters: true,
        qualityNote: "This is the spice where authenticity matters most. Generic 'smoked paprika' often means smoke flavoring added to regular paprika — a different product entirely. The real D.O. La Vera version is transformative.",
      },
      skillDev: { skills: ["seasoning"], difficulty: "easy", proFromScratch: false, fromScratchRecipeId: null },
    },
  },

  {
    ingredient_id: "chili_powder",
    info: {
      description: "A BLEND, not a single spice. Typically ground dried chili peppers + cumin + garlic powder + oregano + sometimes paprika, onion powder, and cocoa. The all-in-one seasoning behind Tex-Mex chili con carne.",
      flavorProfile: "Warm, earthy, mildly spicy, savory. Complex because it IS multiple spices. Much less hot than cayenne — the other ingredients buffer the heat.",
      prepTips: "Bloom in oil with onions and garlic at the start of chili, taco meat, or enchilada sauce. The cumin and oregano in the blend need heat to open up. Toast 30-60 seconds until fragrant — your kitchen will smell like a Texas chili cook-off.",
      storage: {
        location: "pantry",
        shelfLifeDays: 730,
        shelfLife: { fridge: null, freezer: 1095, pantry: 730 },
        tips: "Blends lose potency faster than single spices because the volatile oils from each component degrade at different rates. Replace yearly.",
        spoilageSigns: "No aroma when opened, dust-like texture instead of slightly oily, faded brick-red to brown-gray.",
        freezable: true,
        freezeNotes: "Slows degradation across all the component spices. Adds about a year of useful life.",
      },
      substitutions: [
        { id: "cayenne", tier: "caution", note: "Pure heat, no depth — add cumin + garlic powder + oregano separately to reconstruct. Use 1/5 the amount." },
        { id: "paprika", tier: "emergency", note: "Color but almost no heat or cumin earthiness. Add cumin separately." },
        { id: "taco_seasoning", tier: "creative", note: "Similar territory — both are chili-cumin-based blends. Taco seasoning often has more cumin and added salt." },
      ],
      blendOf: ["cayenne", "cumin", "garlic_powder", "oregano", "paprika"],
      pairs: ["cumin", "garlic", "onion", "beef", "beans", "tomato", "corn", "lime"],
      flavor: {
        primary: ["umami", "spicy"],
        intensity: "moderate",
        heatChange: {
          raw: "dusty, mildly bitter — the dried herbs in the blend need fat and heat",
          cooked: "warm, complex, layered — the components meld and the cumin opens up first",
          charred: "the chili and paprika burn before the cumin scorches; the blend turns acrid quickly",
        },
      },
      nutrition: { per: "1 tsp", kcal: 8, protein_g: 0.3, fat_g: 0.4, carb_g: 1.3, fiber_g: 0.7 },
      origin: "American Southwest / Tex-Mex invention, 19th century. Not traditional to Mexican cuisine — Mexican cooks use individual dried chili varieties (ancho, guajillo, chipotle, pasilla) toasted and ground per dish.",
      culturalNotes: "Gebhardt's Eagle Chili Powder (San Antonio, 1890s) is credited as the first commercial blend. The idea was to make chili con carne accessible without sourcing individual dried peppers. Every brand has a different ratio — some hotter (Mexene), some sweeter, some heavier on cumin (Penzeys medium-hot).",
      recipes: ["Chili con Carne", "Taco Meat", "Enchilada Sauce", "Tex-Mex Rubs", "Frito Pie", "Chili-Lime Popcorn"],
      allergens: ["nightshade"],
      diet: { vegan: true, vegetarian: true, keto: true, halal: true, kosher: "pareve", fodmap: "moderate", nightshade: true, allium: true, glutenFree: true },
      seasonality: { yearRound: true },
      sourcing: "Penzeys 'Medium Hot Chili Powder' is the home-cook benchmark. Mexene is the classic Texas-style. Avoid generic supermarket blends — they're often heavy on filler salt and stale.",
      market: {
        priceTier: "budget",
        availability: "supermarket",
        organicCommon: true,
        qualityMatters: true,
        qualityNote: "The blend quality varies wildly. A good chili powder makes the difference between 'I made chili' and 'this tastes like chili'. Worth the upgrade to Penzeys or a fresh small-batch.",
      },
      skillDev: { skills: ["seasoning", "heat"], difficulty: "easy", proFromScratch: true, fromScratchRecipeId: null },
    },
  },

  // ── Packaging seeds (v0.12.2) ─────────────────────────────────────
  //
  // Per-canonical packaging metadata lets the AddItemModal show chip
  // rows of typical sizes instead of forcing the user to type amounts
  // by hand. Each entry is intentionally minimal — just packaging —
  // because these canonicals weren't previously seeded. The upsert
  // in src/lib/seedIngredientInfo.js uses onConflict: 'ingredient_id',
  // so re-running this seed on an already-seeded row REPLACES the
  // row; keeping these entries minimal means they don't need to carry
  // every other enrichment field we haven't written yet. If any of
  // these canonicals gains richer enrichment later, MERGE into the
  // existing entry rather than creating a duplicate.
  //
  // Shape:
  //   packaging: {
  //     sizes: [{ amount, unit, label }, …],
  //     defaultIndex: <index of the "typical" size>
  //   }
  // Amounts use the canonical's primary unit where possible so the
  // AddItemModal snapshot maps cleanly onto the pantry row's unit.

  // Canned goods — the whole aisle is a narrow set of standard sizes.
  {
    ingredient_id: "canned_tomatoes",
    info: { packaging: {
      sizes: [
        { amount: 14.5, unit: "oz", label: "standard" },
        { amount: 28,   unit: "oz", label: "large" },
        { amount: 6,    unit: "oz", label: "mini" },
      ],
      defaultIndex: 0,
    }},
  },
  {
    ingredient_id: "tomato_paste",
    info: { packaging: {
      sizes: [
        { amount: 6,  unit: "oz", label: "can" },
        { amount: 4.5,unit: "oz", label: "tube" },
        { amount: 18, unit: "oz", label: "jar" },
      ],
      defaultIndex: 0,
    }},
  },
  {
    ingredient_id: "chickpeas",
    info: { packaging: {
      sizes: [
        { amount: 15.5, unit: "oz", label: "can" },
        { amount: 29,   unit: "oz", label: "large can" },
        { amount: 16,   unit: "oz", label: "dry bag" },
      ],
      defaultIndex: 0,
    }},
  },
  {
    ingredient_id: "black_beans",
    info: { packaging: {
      sizes: [
        { amount: 15, unit: "oz", label: "can" },
        { amount: 29, unit: "oz", label: "large can" },
        { amount: 16, unit: "oz", label: "dry bag" },
      ],
      defaultIndex: 0,
    }},
  },
  {
    ingredient_id: "cannellini_beans",
    info: { packaging: {
      sizes: [
        { amount: 15, unit: "oz", label: "can" },
        { amount: 29, unit: "oz", label: "large can" },
      ],
      defaultIndex: 0,
    }},
  },
  {
    ingredient_id: "kidney_beans",
    info: { packaging: {
      sizes: [
        { amount: 15, unit: "oz", label: "can" },
        { amount: 29, unit: "oz", label: "large can" },
        { amount: 16, unit: "oz", label: "dry bag" },
      ],
      defaultIndex: 0,
    }},
  },
  {
    ingredient_id: "pinto_beans",
    info: { packaging: {
      sizes: [
        { amount: 15, unit: "oz", label: "can" },
        { amount: 29, unit: "oz", label: "large can" },
        { amount: 16, unit: "oz", label: "dry bag" },
      ],
      defaultIndex: 0,
    }},
  },
  {
    ingredient_id: "tuna",
    info: { packaging: {
      sizes: [
        { amount: 5,  unit: "oz", label: "single can" },
        { amount: 12, unit: "oz", label: "family can" },
        { amount: 2.6,unit: "oz", label: "pouch" },
      ],
      defaultIndex: 0,
    }},
  },

  // Oils / vinegars / condiments — bottle + jar country.
  {
    ingredient_id: "olive_oil",
    info: { packaging: {
      sizes: [
        { amount: 17,  unit: "fl_oz", label: "standard (500ml)" },
        { amount: 8.4, unit: "fl_oz", label: "small (250ml)" },
        { amount: 33.8,unit: "fl_oz", label: "1 liter" },
      ],
      defaultIndex: 0,
    }},
  },
  {
    ingredient_id: "balsamic",
    info: { packaging: {
      sizes: [
        { amount: 8.5,  unit: "fl_oz", label: "standard (250ml)" },
        { amount: 17,   unit: "fl_oz", label: "500ml" },
        { amount: 5,    unit: "fl_oz", label: "traditional (aged)" },
      ],
      defaultIndex: 0,
    }},
  },
  {
    ingredient_id: "vinegar",
    info: { packaging: {
      sizes: [
        { amount: 16, unit: "fl_oz", label: "pint" },
        { amount: 32, unit: "fl_oz", label: "quart" },
        { amount: 8,  unit: "fl_oz", label: "small" },
      ],
      defaultIndex: 0,
    }},
  },
  {
    ingredient_id: "peanut_butter",
    info: { packaging: {
      sizes: [
        { amount: 16, unit: "oz", label: "standard jar" },
        { amount: 28, unit: "oz", label: "large jar" },
        { amount: 40, unit: "oz", label: "value" },
      ],
      defaultIndex: 0,
    }},
  },
  {
    ingredient_id: "honey",
    info: { packaging: {
      sizes: [
        { amount: 12, unit: "oz", label: "standard bear" },
        { amount: 24, unit: "oz", label: "large" },
        { amount: 16, unit: "oz", label: "jar" },
      ],
      defaultIndex: 0,
    }},
  },
  {
    ingredient_id: "maple_syrup",
    info: { packaging: {
      sizes: [
        { amount: 8,  unit: "fl_oz", label: "small" },
        { amount: 12.5, unit: "fl_oz", label: "standard" },
        { amount: 32, unit: "fl_oz", label: "quart jug" },
      ],
      defaultIndex: 1,
    }},
  },
  {
    ingredient_id: "ketchup",
    info: { packaging: {
      sizes: [
        { amount: 20, unit: "oz", label: "squeeze bottle" },
        { amount: 38, unit: "oz", label: "family size" },
        { amount: 14, unit: "oz", label: "small" },
      ],
      defaultIndex: 0,
    }},
  },
  {
    ingredient_id: "mayo",
    info: { packaging: {
      sizes: [
        { amount: 30, unit: "oz", label: "standard jar" },
        { amount: 15, unit: "oz", label: "small jar" },
        { amount: 48, unit: "oz", label: "value" },
      ],
      defaultIndex: 0,
    }},
  },
  {
    ingredient_id: "mustard",
    info: { packaging: {
      sizes: [
        { amount: 12, unit: "oz", label: "squeeze" },
        { amount: 8,  unit: "oz", label: "jar" },
        { amount: 20, unit: "oz", label: "family" },
      ],
      defaultIndex: 0,
    }},
  },
  {
    ingredient_id: "soy_sauce",
    info: { packaging: {
      sizes: [
        { amount: 10, unit: "fl_oz", label: "standard" },
        { amount: 15, unit: "fl_oz", label: "large" },
        { amount: 5,  unit: "fl_oz", label: "small" },
      ],
      defaultIndex: 0,
    }},
  },
  {
    ingredient_id: "hot_sauce",
    info: { packaging: {
      sizes: [
        { amount: 5,  unit: "fl_oz", label: "standard" },
        { amount: 12, unit: "fl_oz", label: "large" },
        { amount: 3,  unit: "fl_oz", label: "mini" },
      ],
      defaultIndex: 0,
    }},
  },
  {
    ingredient_id: "sriracha",
    info: { packaging: {
      sizes: [
        { amount: 17, unit: "fl_oz", label: "iconic rooster" },
        { amount: 28, unit: "fl_oz", label: "large" },
        { amount: 9,  unit: "fl_oz", label: "small" },
      ],
      defaultIndex: 0,
    }},
  },

  // Dry pantry staples — box / bag world.
  {
    ingredient_id: "rice",
    info: { packaging: {
      sizes: [
        { amount: 5,  unit: "lb", label: "standard bag" },
        { amount: 2,  unit: "lb", label: "small bag" },
        { amount: 20, unit: "lb", label: "bulk" },
      ],
      defaultIndex: 0,
    }},
  },
  {
    ingredient_id: "spaghetti",
    info: { packaging: {
      sizes: [
        { amount: 16, unit: "oz", label: "1 lb box" },
        { amount: 24, unit: "oz", label: "family box" },
      ],
      defaultIndex: 0,
    }},
  },
  {
    ingredient_id: "penne",
    info: { packaging: {
      sizes: [
        { amount: 16, unit: "oz", label: "1 lb box" },
        { amount: 24, unit: "oz", label: "family box" },
      ],
      defaultIndex: 0,
    }},
  },
  {
    ingredient_id: "flour",
    info: { packaging: {
      sizes: [
        { amount: 5,  unit: "lb", label: "standard bag" },
        { amount: 2,  unit: "lb", label: "small bag" },
        { amount: 10, unit: "lb", label: "bulk" },
      ],
      defaultIndex: 0,
    }},
  },
  {
    ingredient_id: "sugar",
    info: { packaging: {
      sizes: [
        { amount: 4, unit: "lb", label: "standard bag" },
        { amount: 2, unit: "lb", label: "small" },
        { amount: 10, unit: "lb", label: "bulk" },
      ],
      defaultIndex: 0,
    }},
  },
  {
    ingredient_id: "oats",
    info: { packaging: {
      sizes: [
        { amount: 18, unit: "oz", label: "standard canister" },
        { amount: 42, unit: "oz", label: "large" },
        { amount: 64, unit: "oz", label: "family" },
      ],
      defaultIndex: 0,
    }},
  },
  {
    ingredient_id: "quinoa",
    info: { packaging: {
      sizes: [
        { amount: 16, unit: "oz", label: "1 lb bag" },
        { amount: 32, unit: "oz", label: "2 lb bag" },
      ],
      defaultIndex: 0,
    }},
  },
  {
    ingredient_id: "lentils",
    info: { packaging: {
      sizes: [
        { amount: 16, unit: "oz", label: "1 lb bag" },
        { amount: 32, unit: "oz", label: "2 lb bag" },
      ],
      defaultIndex: 0,
    }},
  },

  // Dairy — gallon / quart / brick standards.
  {
    ingredient_id: "milk",
    info: { packaging: {
      sizes: [
        { amount: 1,   unit: "gallon",      label: "gallon" },
        { amount: 1,   unit: "half_gallon", label: "half-gallon" },
        { amount: 1,   unit: "quart",       label: "quart" },
        { amount: 1,   unit: "pint",        label: "pint" },
      ],
      defaultIndex: 0,
    }},
  },
  {
    ingredient_id: "heavy_cream",
    info: { packaging: {
      sizes: [
        { amount: 1, unit: "pint",  label: "pint" },
        { amount: 1, unit: "quart", label: "quart" },
        { amount: 1, unit: "cup",   label: "cup" },
      ],
      defaultIndex: 0,
    }},
  },
  {
    ingredient_id: "butter",
    info: { packaging: {
      sizes: [
        { amount: 4, unit: "stick", label: "1 lb box (4 sticks)" },
        { amount: 2, unit: "stick", label: "half lb (2 sticks)" },
        { amount: 1, unit: "stick", label: "single stick" },
      ],
      defaultIndex: 0,
    }},
  },

  // Proteins — tray / pack world.
  {
    ingredient_id: "bacon",
    info: { packaging: {
      sizes: [
        { amount: 12, unit: "oz", label: "standard pack" },
        { amount: 16, unit: "oz", label: "1 lb" },
        { amount: 24, unit: "oz", label: "family" },
      ],
      defaultIndex: 0,
    }},
  },
  {
    ingredient_id: "hot_dog",
    info: { packaging: {
      sizes: [
        { amount: 8,  unit: "count", label: "8-pack" },
        { amount: 10, unit: "count", label: "10-pack" },
        { amount: 16, unit: "count", label: "value pack" },
      ],
      defaultIndex: 0,
    }},
  },
  {
    ingredient_id: "ground_beef",
    info: { packaging: {
      sizes: [
        { amount: 1, unit: "lb", label: "1 lb tray" },
        { amount: 2, unit: "lb", label: "2 lb family" },
        { amount: 3, unit: "lb", label: "3 lb value" },
      ],
      defaultIndex: 0,
    }},
  },

  // Eggs — the only "count" canonical where cartons are everything.
  {
    ingredient_id: "eggs",
    info: { packaging: {
      sizes: [
        { amount: 1, unit: "dozen", label: "dozen" },
        { amount: 18, unit: "count", label: "18-count" },
        { amount: 6, unit: "count", label: "half-dozen" },
      ],
      defaultIndex: 0,
    }},
  },

  // Bread — loaf standard.
  {
    ingredient_id: "bread",
    info: { packaging: {
      sizes: [
        { amount: 20, unit: "oz", label: "standard loaf" },
        { amount: 24, unit: "oz", label: "bakery loaf" },
        { amount: 12, unit: "oz", label: "small loaf" },
      ],
      defaultIndex: 0,
    }},
  },
];
